import { createModifiers } from '@carboncopyco/editor-utils';
import type { CustomModifier, TransactionModifier } from '@carboncopyco/types';
import { pick } from 'lodash-es';
import { useCallback } from 'react';
import type { TransactionModifiersForm } from './types';

type FormFields = {
  [key in keyof TransactionModifiersForm]?: unknown;
};

const modifiersFormFields: (keyof TransactionModifiersForm)[] = [
  'response',
  'headers',
  'representationType',
  'representationFields',
  'dynamicResponseModifiers',
  'customModifier',
];

type UseModifiersBuilderProps = {
  getFormValues: () => TransactionModifiersForm;
};

export const useModifiersBuilder = ({
  getFormValues,
}: UseModifiersBuilderProps) => {
  const isModifiersFormFieldsDirty = useCallback((formFields: FormFields) => {
    return !!Object.keys(pick(formFields, modifiersFormFields)).length;
  }, []);

  const buildModifiers = useCallback((): TransactionModifier[] => {
    const {
      response,
      responseType,
      headers,
      representationType,
      representationFields,
      dynamicResponseModifiers,
      customModifier,
    } = getFormValues();

    const formFields = {
      static: () =>
        createModifiers('static', {
          response,
          headers,
          representationType,
          representationFields,
        }),
      dynamic: () =>
        createModifiers(
          'dynamic',
          dynamicResponseModifiers.map((m) => ({
            ...m.arguments,
          }))
        ),
      custom: () => {
        const modifiers = JSON.parse(customModifier) as CustomModifier[];
        return modifiers.map((modifier) => ({
          ...modifier,
          arguments: {
            ...modifier.arguments,
            isCustomModifier: true,
          },
        }));
      },
    } as const;

    return formFields[responseType]?.();
  }, [getFormValues]);

  return {
    isModifiersFormFieldsDirty,
    buildModifiers,
  } as const;
};
